import React, { useEffect, useState, useRef, useContext } from "react";
import {
  Button,
  OverlayTrigger,
  Tooltip,
  Col,
  Card,
  Row,
  Badge,
  Modal,
  Table,
  Form,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import {
  IconSettings,
  IconTrash,
  IconPlus,
  IconWorld,
  IconCalendar,
  IconWifi,
  IconBrandGoogle,
  IconUpload,
  IconPower,
} from "@tabler/icons-react";
import DefaultLoader from "../../jsx/components/Loader";
import Swal from "sweetalert2";
import AlertResponses from "../../jsx/components/Alert";
import UserContext from "../../contexts/UserContext";

import { ListAllIntegrations } from "../../services/IntegrationsServices";
import {
  ListGoogleCalendarIntegrations,
  CreateGoogleCalendarIntegration,
  TestGoogleCalendarConnection,
  DeleteGoogleCalendarIntegration,
  UpdateGoogleCalendarIntegration,
} from "../../services/GoogleCalendarServices";
import NotificationsContext from "../../contexts/NotificationsContext";

export default function Integrations() {
  const { user } = useContext(UserContext);
  const { notify, setNotify } = useContext(NotificationsContext);
  const [isLoading, setIsLoading] = useState(true);
  const [showGoogleCalendarModal, setShowGoogleCalendarModal] = useState(false);
  const [integrationStatistics, setIntegrationStatistics] = useState(null);
  const [googleCalendarIntegrations, setGoogleCalendarIntegrations] = useState(
    []
  );
  const [selectedGoogleCalendar, setSelectedGoogleCalendar] = useState(null);
  const [uploadingCredentials, setUploadingCredentials] = useState(false);
  const [integrationName, setIntegrationName] = useState("");
  const [calendarId, setCalendarId] = useState("primary");
  const [credentialsFile, setCredentialsFile] = useState(null);
  const [originalFileObject, setOriginalFileObject] = useState(null);

  const fileInputRef = useRef(null);

  // Função utilitária para extrair mensagens de erro da API
  const getApiErrorMessage = (
    error,
    defaultMessage = "Ocorreu um erro inesperado."
  ) => {
    if (error?.data?.error) {
      return error.data.error;
    } else if (error?.response?.data?.error) {
      return error.response.data.error;
    } else if (error?.data?.message) {
      return error.data.message;
    } else if (error?.response?.data?.message) {
      return error.response.data.message;
    } else if (error?.message) {
      return error.message;
    }
    return defaultMessage;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo do arquivo
    if (file.type !== "application/json") {
      setNotify({
        title: "Erro no arquivo",
        message: "Por favor, selecione um arquivo JSON válido.",
        type: "error",
        visible: true,
      });
      return;
    }

    // Validar tamanho do arquivo (máximo 5MB)
    const maxSizeBytes = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSizeBytes) {
      setNotify({
        title: "Arquivo muito grande",
        message: "O arquivo deve ter no máximo 5MB.",
        type: "error",
        visible: true,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonContent = JSON.parse(e.target.result);

        // Validar estrutura básica do arquivo JSON
        const requiredFields = [
          "type",
          "project_id",
          "private_key",
          "client_email",
          "auth_uri",
          "token_uri",
        ];
        const missingFields = requiredFields.filter(
          (field) => !jsonContent[field]
        );

        if (missingFields.length > 0) {
          setNotify({
            title: "Arquivo JSON inválido",
            message: `Campos obrigatórios ausentes: ${missingFields.join(
              ", "
            )}`,
            type: "error",
            visible: true,
          });
          return;
        }

        if (jsonContent.type !== "service_account") {
          setNotify({
            title: "Tipo de conta inválido",
            message:
              "O arquivo deve ser de uma conta de serviço (service_account).",
            type: "error",
            visible: true,
          });
          return;
        }

        // Armazenar tanto o conteúdo JSON quanto o arquivo original
        setCredentialsFile(jsonContent);
        setOriginalFileObject(file);
        setNotify({
          title: "Arquivo carregado!",
          message:
            "Credenciais validadas com sucesso. Agora você pode criar a integração.",
          type: "success",
          visible: true,
        });
      } catch (error) {
        setNotify({
          title: "Erro ao ler arquivo",
          message: "Arquivo JSON inválido ou corrompido.",
          type: "error",
          visible: true,
        });
      }
    };

    reader.readAsText(file);
  };

  const handleCreateIntegration = async () => {
    if (!credentialsFile || !originalFileObject) {
      setNotify({
        title: "Credenciais necessárias",
        message: "Por favor, faça upload do arquivo de credenciais primeiro.",
        type: "error",
        visible: true,
      });
      return;
    }

    if (!integrationName.trim()) {
      setNotify({
        title: "Nome necessário",
        message: "Por favor, informe um nome para a integração.",
        type: "error",
        visible: true,
      });
      return;
    }

    if (!calendarId.trim()) {
      setNotify({
        title: "Calendar ID necessário",
        message: "Por favor, informe o Calendar ID.",
        type: "error",
        visible: true,
      });
      return;
    }

    setUploadingCredentials(true);

    try {
      // Preparar FormData para enviar o arquivo
      const formData = new FormData();
      formData.append("name", integrationName.trim());
      formData.append("integration_type", "google_calendar");
      formData.append("credentials_file", originalFileObject);
      formData.append("calendar_id", calendarId);

      // Chamada real para a API
      await CreateGoogleCalendarIntegration(formData);

      // Limpar formulário
      setIntegrationName("");
      setCalendarId("primary");
      setCredentialsFile(null);
      setOriginalFileObject(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setNotify({
        title: "Integração criada!",
        message:
          "Integração Google Agenda criada com sucesso. Teste a conexão para ativá-la.",
        type: "success",
        visible: true,
      });

      // Recarregar lista de integrações
      setTimeout(() => {
        loadAllIntegrations();
      }, 1000);
    } catch (error) {
      console.error("Erro ao criar integração:", error);

      // Tratamento especial para erro 403 (plano insuficiente)
      if (error?.response?.status === 403) {
        setNotify({
          title: "Plano insuficiente",
          message:
            "Sua conta não possui um plano compatível com esta funcionalidade. Atualize para PRO ou Business.",
          type: "error",
          visible: true,
        });
        return;
      }

      setNotify({
        title: "Erro ao criar integração",
        message: getApiErrorMessage(
          error,
          "Ocorreu um erro ao criar a integração. Tente novamente."
        ),
        type: "error",
        visible: true,
      });
    } finally {
      setUploadingCredentials(false);
    }
  };

  const handleActivateIntegration = async (integration) => {
    const result = await Swal.fire({
      title: "Ativar Integração",
      html: `
        <div style="text-align: left; margin: 20px 0;">
          <p><strong>Integração:</strong> ${integration.name}</p>
          <p><strong>Tipo:</strong> Google Calendar</p>
          <br>
          <p style="color: #6c757d; font-size: 14px;">
            Tem certeza que deseja ativar esta integração? 
            Ela ficará disponível para conexão com agentes.
          </p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, ativar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
    });

    if (result.isConfirmed) {
      try {
        setNotify({
          title: "Ativando integração...",
          message: "Aguarde enquanto ativamos a integração",
          type: "info",
          visible: true,
        });

        await UpdateGoogleCalendarIntegration(integration.id, {
          status: "active",
        });

        setTimeout(() => {
          window.location.reload();
        }, 2500);

        setNotify({
          title: "Integração ativada!",
          message:
            "A integração foi ativada com sucesso e está disponível para uso.",
          type: "success",
          visible: true,
        });
      } catch (error) {
        console.error("Erro ao ativar integração:", error);

        setNotify({
          title: "Erro ao ativar",
          message: getApiErrorMessage(
            error,
            "Não foi possível ativar a integração."
          ),
          type: "error",
          visible: true,
        });
      }
    }
  };

  const handleTestConnection = async (integration) => {
    try {
      setNotify({
        title: "Testando conexão...",
        message: "Verificando acesso ao Google Agenda...",
        type: "info",
        visible: true,
      });

      const result = await TestGoogleCalendarConnection(integration.id);

      if (result.success) {
        // Atualizar integração com sucesso
        const updatedIntegration = {
          ...integration,
          status: "active",
          status_display: "Ativa",
          last_sync: new Date().toISOString(),
          last_error: null,
        };

        setGoogleCalendarIntegrations((prev) =>
          prev.map((item) =>
            item.id === integration.id ? updatedIntegration : item
          )
        );

        if (selectedGoogleCalendar?.id === integration.id) {
          setSelectedGoogleCalendar(updatedIntegration);
        }

        setNotify({
          title: "Conexão bem-sucedida!",
          message:
            result.message ||
            "A integração foi ativada e está funcionando corretamente.",
          type: "success",
          visible: true,
        });
      } else {
        // Erro na conexão
        const updatedIntegration = {
          ...integration,
          status: "error",
          status_display: "Erro",
          last_error: result.message,
        };

        setGoogleCalendarIntegrations((prev) =>
          prev.map((item) =>
            item.id === integration.id ? updatedIntegration : item
          )
        );

        if (selectedGoogleCalendar?.id === integration.id) {
          setSelectedGoogleCalendar(updatedIntegration);
        }

        setNotify({
          title: "Erro na conexão",
          message:
            result.message || "Não foi possível conectar com o Google Agenda.",
          type: "error",
          visible: true,
        });
      }
    } catch (error) {
      console.error("Erro ao testar conexão:", error);

      setNotify({
        title: "Erro na conexão",
        message: getApiErrorMessage(
          error,
          "Erro desconhecido ao testar conexão."
        ),
        type: "error",
        visible: true,
      });
    }
  };

  const handleDeleteIntegration = (integration) => {
    Swal.fire({
      title: "Você tem certeza?",
      text: "Você está prestes a excluir a integração com o Google Agenda!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Excluir Integração",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Chamada real para a API
          await DeleteGoogleCalendarIntegration(integration.id);

          // Remover integração da lista
          setGoogleCalendarIntegrations((prev) =>
            prev.filter((item) => item.id !== integration.id)
          );

          if (selectedGoogleCalendar?.id === integration.id) {
            setSelectedGoogleCalendar(null);
          }

          setNotify({
            title: "Integração excluída!",
            message: "A integração Google Agenda foi removida com sucesso.",
            type: "success",
            visible: true,
          });
        } catch (error) {
          console.error("Erro ao excluir integração:", error);

          setNotify({
            title: "Erro ao excluir",
            message: getApiErrorMessage(
              error,
              "Não foi possível excluir a integração."
            ),
            type: "error",
            visible: true,
          });
        }
      }
    });
  };

  const loadAllIntegrations = async () => {
    try {
      const response = await ListAllIntegrations();
      // Store all integrations in case needed for future features
      setIntegrationStatistics(response.statistics || null);

      // Filtrar apenas integrações Google Calendar
      const googleIntegrations = (response.integrations || []).filter(
        (integration) => integration.integration_type === "google_calendar"
      );
      setGoogleCalendarIntegrations(googleIntegrations);

      if (googleIntegrations.length > 0) {
        setSelectedGoogleCalendar(googleIntegrations[0]);
      }
    } catch (error) {
      // Fallback para endpoint específico se o geral falhar
      try {
        const integrations = await ListGoogleCalendarIntegrations();
        setGoogleCalendarIntegrations(integrations || []);
        if (integrations && integrations.length > 0) {
          setSelectedGoogleCalendar(integrations[0]);
        }
      } catch (fallbackError) {
        console.log("Nenhuma integração encontrada");
        setGoogleCalendarIntegrations([]);
        setSelectedGoogleCalendar(null);
      }
    }
  };

  useEffect(() => {
    // Carregar todas as integrações
    loadAllIntegrations();

    setIsLoading(false);
  }, []);

  return (
    <>
      {isLoading ? (
        <DefaultLoader isLoading={isLoading} />
      ) : (
        <section id="channels">
          <div className="col-xxl-12 pl-0 mb-4">
            <h2>Integrações</h2>
            <p className="text-muted">
              Integre seu Agente de IA com serviços externos e plataformas
              populares
            </p>
            {integrationStatistics && (
              <div className="row g-3 mb-4">
                <div className="col-md-3">
                  <div className="card border-0 bg-light">
                    <div className="card-body text-center py-2">
                      <div className="h4 mb-0">
                        {integrationStatistics.total}
                      </div>
                      <small className="text-muted">Total</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 bg-light">
                    <div className="card-body text-center py-2">
                      <div className="h4 mb-0 text-success">
                        {integrationStatistics.by_status?.active || 0}
                      </div>
                      <small className="text-muted">Ativas</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 bg-light">
                    <div className="card-body text-center py-2">
                      <div className="h4 mb-0 text-warning">
                        {integrationStatistics.by_status?.inactive || 0}
                      </div>
                      <small className="text-muted">Inativas</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 bg-light">
                    <div className="card-body text-center py-2">
                      <div className="h4 mb-0 text-danger">
                        {integrationStatistics.by_status?.error || 0}
                      </div>
                      <small className="text-muted">Com Erro</small>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <Row className="g-4">
            {/* Google Agenda Integration Card */}
            <Col lg={4} md={6} sm={12}>
              <Card className="h-100 integration-card">
                <Card.Header className="d-flex align-items-center">
                  <div className="integration-icon me-3">
                    <IconBrandGoogle size={32} className="text-danger" />
                  </div>
                  <div>
                    <Card.Title className="mb-1">Google Agenda</Card.Title>
                    <small className="text-muted">
                      Sincronização de Agenda
                    </small>
                  </div>
                </Card.Header>
                <Card.Body>
                  {googleCalendarIntegrations &&
                  googleCalendarIntegrations.length > 0 ? (
                    <div>
                      <div className="mb-3">
                        <small className="text-muted">
                          Integrações ativas:
                        </small>
                        <div className="mt-1">
                          <Badge bg="primary">
                            {googleCalendarIntegrations.length}{" "}
                            {googleCalendarIntegrations.length === 1
                              ? "integração"
                              : "integrações"}
                          </Badge>
                        </div>
                      </div>

                      {selectedGoogleCalendar && (
                        <>
                          <div className="mb-3">
                            <small className="text-muted">Status:</small>
                            <div className="mt-1">
                              <Badge
                                bg={
                                  selectedGoogleCalendar.status === "active"
                                    ? "success"
                                    : selectedGoogleCalendar.status ===
                                      "inactive"
                                    ? "secondary"
                                    : "danger"
                                }
                              >
                                {selectedGoogleCalendar.status_display ||
                                  selectedGoogleCalendar.status}
                              </Badge>
                            </div>
                          </div>

                          <div className="mb-3">
                            <small className="text-muted">Nome:</small>
                            <div className="mt-1">
                              <strong>{selectedGoogleCalendar.name}</strong>
                            </div>
                          </div>

                          <div className="mb-3">
                            <small className="text-muted">Calendar ID:</small>
                            <div className="mt-1">
                              <small className="text-break">
                                {selectedGoogleCalendar.data?.calendar_id ||
                                  selectedGoogleCalendar.calendar_id}
                              </small>
                            </div>
                          </div>

                          {selectedGoogleCalendar.data
                            ?.service_account_email && (
                            <div className="mb-3">
                              <small className="text-muted">
                                Email da Conta:
                              </small>
                              <div className="mt-1">
                                <small className="text-break">
                                  {
                                    selectedGoogleCalendar.data
                                      .service_account_email
                                  }
                                </small>
                              </div>
                            </div>
                          )}

                          <div className="mb-3">
                            <small className="text-muted">
                              Última sincronização:
                            </small>
                            <div className="mt-1">
                              <small>
                                {selectedGoogleCalendar.last_sync
                                  ? new Date(
                                      selectedGoogleCalendar.last_sync
                                    ).toLocaleString()
                                  : "Nunca"}
                              </small>
                            </div>
                          </div>

                          {selectedGoogleCalendar.last_error && (
                            <div className="mb-3">
                              <small className="text-muted">Último erro:</small>
                              <div className="mt-1">
                                <small className="text-danger">
                                  {selectedGoogleCalendar.last_error}
                                </small>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <IconBrandGoogle size={48} className="text-muted mb-3" />
                      <p className="text-muted">
                        Nenhuma integração configurada
                      </p>
                    </div>
                  )}
                </Card.Body>
                <Card.Footer className="bg-transparent">
                  {googleCalendarIntegrations &&
                  googleCalendarIntegrations.length > 0 ? (
                    <Button
                      className="w-100"
                      variant="primary"
                      onClick={() => setShowGoogleCalendarModal(true)}
                    >
                      <IconSettings size={16} className="me-2" />
                      Gerenciar
                    </Button>
                  ) : (
                    <Button
                      className="w-100"
                      variant="outline-primary"
                      onClick={() => setShowGoogleCalendarModal(true)}
                      disabled={!user}
                    >
                      <IconPlus size={16} className="me-2" />
                      Conectar
                    </Button>
                  )}
                </Card.Footer>
              </Card>
            </Col>

            {/* Website Integration Card (Coming Soon) */}
            {/*  <Col lg={4} md={6} sm={12}>
              <Card className="h-100 integration-card opacity-75">
                <Card.Header className="d-flex align-items-center">
                  <div className="integration-icon me-3">
                    <IconWorld size={32} className="text-primary" />
                  </div>
                  <div>
                    <Card.Title className="mb-1">Website</Card.Title>
                    <small className="text-muted">Chat Web Widget</small>
                  </div>
                </Card.Header>
                
                <Card.Body>
                  <div className="text-center py-4">
                    <IconWorld size={48} className="text-muted mb-3" />
                    <h5>Em Desenvolvimento</h5>
                    <p className="text-muted small">
                      Widget de chat para seu website com integração completa ao IA
                    </p>
                  </div>
                </Card.Body>
                
                <Card.Footer className="bg-transparent">
                  <Button className="w-100" disabled variant="outline-primary">
                    <IconClock size={16} className="me-2" />
                    Em Breve
                  </Button>
                </Card.Footer>
              </Card>
            </Col> */}

            {/* Instagram Integration Card (Coming Soon) */}
            {/*  <Col lg={4} md={6} sm={12}>
              <Card className="h-100 integration-card opacity-75">
                <Card.Header className="d-flex align-items-center">
                  <div className="integration-icon me-3">
                    <IconBrandInstagram size={32} className="text-danger" />
                  </div>
                  <div>
                    <Card.Title className="mb-1">Instagram</Card.Title>
                    <small className="text-muted">Direct Messages</small>
                  </div>
                </Card.Header>
                
                <Card.Body>
                  <div className="text-center py-4">
                    <IconBrandInstagram size={48} className="text-muted mb-3" />
                    <h5>Em Desenvolvimento</h5>
                    <p className="text-muted small">
                      Responda automaticamente as mensagens diretas do Instagram
                    </p>
                  </div>
                </Card.Body>
                
                <Card.Footer className="bg-transparent">
                  <Button className="w-100" disabled variant="outline-danger">
                    <IconClock size={16} className="me-2" />
                    Em Breve
                  </Button>
                </Card.Footer>
              </Card>
            </Col> */}
          </Row>

          {notify.visible && (
            <AlertResponses
              title={notify.title}
              message={notify.message}
              type={notify.type}
              visible={notify.visible}
              setNotify={setNotify}
            />
          )}

          {/* Modal de Gerenciamento do Google Agenda */}
          <Modal
            show={showGoogleCalendarModal}
            onHide={() => setShowGoogleCalendarModal(false)}
            size="xl"
            centered
            enforceFocus={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>
                <IconBrandGoogle size={24} className="me-2 text-danger" />
                Gerenciar Google Agenda
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {googleCalendarIntegrations &&
              googleCalendarIntegrations.length > 0 ? (
                <div>
                  <div className="mb-4">
                    <h5 className="mb-3">Integração Ativa</h5>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Nome</th>
                          <th>Calendar ID</th>
                          <th>Status</th>
                          <th>Criada em</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {googleCalendarIntegrations.map((integration) => (
                          <tr key={integration.id}>
                            <td>
                              <strong>{integration.name}</strong>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <IconCalendar size={16} className="me-2" />
                                <small className="text-break">
                                  {integration.primary_identifier}
                                </small>
                              </div>
                            </td>
                            <td>
                              <Badge
                                bg={
                                  integration.status === "active"
                                    ? "success"
                                    : integration.status === "inactive"
                                    ? "secondary"
                                    : "danger"
                                }
                              >
                                {integration.status_display ||
                                  integration.status}
                              </Badge>
                            </td>

                            <td>
                              <small>
                                {new Date(
                                  integration.created_at
                                ).toLocaleString()}
                              </small>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <OverlayTrigger
                                  overlay={<Tooltip>Testar Conexão</Tooltip>}
                                >
                                  <Button
                                    size="sm"
                                    variant={
                                      integration.status === "active"
                                        ? "success"
                                        : "info"
                                    }
                                    onClick={() =>
                                      handleTestConnection(integration)
                                    }
                                  >
                                    <IconWifi size={14} />
                                  </Button>
                                </OverlayTrigger>

                                {integration.status !== "active" && (
                                  <OverlayTrigger
                                    overlay={
                                      <Tooltip>Ativar Integração</Tooltip>
                                    }
                                  >
                                    <Button
                                      size="sm"
                                      variant="success"
                                      onClick={() =>
                                        handleActivateIntegration(integration)
                                      }
                                    >
                                      <IconPower size={14} />
                                    </Button>
                                  </OverlayTrigger>
                                )}

                                <OverlayTrigger
                                  overlay={
                                    <Tooltip>Configurar Calendar ID</Tooltip>
                                  }
                                >
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={async () => {
                                      const currentCalendarId =
                                        integration.data?.calendar_id ||
                                        integration.calendar_id;

                                      // Temporariamente esconder o modal principal para evitar conflitos de z-index
                                      const modalElement =
                                        document.querySelector(".modal.show");
                                      if (modalElement) {
                                        modalElement.style.zIndex = "1040";
                                      }

                                      const { value: newCalendarId } =
                                        await Swal.fire({
                                          title: "Configurar Calendar ID",
                                          html: `
                                          <div class="text-start mb-3">
                                            <p class="mb-2">Digite o novo Calendar ID para a integração:</p>
                                            <small class="text-muted">
                                              • Use "primary" para o calendário principal<br>
                                              • Ou insira o ID específico do calendário que deseja sincronizar
                                            </small>
                                          </div>
                                        `,
                                          target: document.body,
                                          input: "text",
                                          inputValue: currentCalendarId,
                                          inputPlaceholder:
                                            "Ex: primary, exemplo@gmail.com",
                                          showCancelButton: true,
                                          confirmButtonText: "Atualizar",
                                          cancelButtonText: "Cancelar",
                                          confirmButtonColor: "#0d6efd",
                                          cancelButtonColor: "#6c757d",
                                          // Configurações para funcionar dentro do modal Bootstrap
                                          backdrop: true,
                                          allowOutsideClick: false,
                                          allowEscapeKey: true,
                                          focusConfirm: false,
                                          // Z-index alto para ficar acima do modal Bootstrap
                                          customClass: {
                                            container: "swal-high-zindex",
                                            popup: "swal-high-zindex",
                                          },
                                          didOpen: () => {
                                            // Forçar foco no input após abrir
                                            const input = Swal.getInput();
                                            if (input) {
                                              setTimeout(() => {
                                                input.focus();
                                                input.select();
                                              }, 100);
                                            }
                                          },
                                          inputValidator: (value) => {
                                            if (!value || !value.trim()) {
                                              return "Por favor, digite um Calendar ID válido!";
                                            }
                                            if (
                                              value.trim() === currentCalendarId
                                            ) {
                                              return "O Calendar ID deve ser diferente do atual!";
                                            }
                                          },
                                        });

                                      // Restaurar z-index do modal principal
                                      if (modalElement) {
                                        modalElement.style.zIndex = "";
                                      }

                                      if (
                                        newCalendarId &&
                                        newCalendarId.trim()
                                      ) {
                                        try {
                                          // Mostrar loading
                                          Swal.fire({
                                            title: "Atualizando...",
                                            html: "Configurando novo Calendar ID",
                                            allowOutsideClick: false,
                                            didOpen: () => {
                                              Swal.showLoading();
                                            },
                                          });

                                          // Chamada real para a API
                                          await UpdateGoogleCalendarIntegration(
                                            integration.id,
                                            {
                                              calendar_id: newCalendarId.trim(),
                                            }
                                          );

                                          setTimeout(() => {
                                            window.location.reload();
                                          }, 2500);

                                          // Fechar loading e mostrar sucesso
                                          Swal.fire({
                                            title: "Sucesso!",
                                            text: "Calendar ID atualizado com sucesso. Teste a conexão para ativar com o novo calendário.",
                                            icon: "success",
                                            confirmButtonText: "Ok",
                                            confirmButtonColor: "#198754",
                                          });
                                        } catch (error) {
                                          console.error(
                                            "Erro ao atualizar Calendar ID:",
                                            error
                                          );

                                          // Mostrar erro no Swal
                                          Swal.fire({
                                            title: "Erro ao atualizar",
                                            text: getApiErrorMessage(
                                              error,
                                              "Erro ao atualizar Calendar ID."
                                            ),
                                            icon: "error",
                                            confirmButtonText: "Ok",
                                            confirmButtonColor: "#dc3545",
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    <IconSettings size={14} />
                                  </Button>
                                </OverlayTrigger>

                                <OverlayTrigger
                                  overlay={<Tooltip>Deletar</Tooltip>}
                                >
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() =>
                                      handleDeleteIntegration(integration)
                                    }
                                  >
                                    <IconTrash size={14} />
                                  </Button>
                                </OverlayTrigger>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {selectedGoogleCalendar &&
                    selectedGoogleCalendar.last_error && (
                      <div className="alert alert-danger">
                        <strong>Último erro:</strong>{" "}
                        {selectedGoogleCalendar.last_error}
                      </div>
                    )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <IconBrandGoogle size={64} className="text-muted mb-3" />
                  <h5>Nenhuma integração configurada</h5>
                  <p className="text-muted mb-4">
                    Conecte sua conta do Google Agenda para sincronizar eventos
                    automaticamente com seu agente IA.
                  </p>

                  {user ? (
                    <div>
                      <div className="mb-4 p-3 bg-light rounded">
                        <h6 className="mb-3">Como configurar:</h6>
                        <ol className="text-start">
                          <li>
                            Crie uma conta de serviço no Google Cloud Console
                          </li>
                          <li>Ative a API do Google Agenda</li>
                          <li>Baixe o arquivo JSON com as credenciais</li>
                          <li>
                            Faça upload do arquivo usando o formulário abaixo
                          </li>
                          <li>
                            Configure o ID do calendário que deseja sincronizar
                          </li>
                        </ol>
                      </div>

                      <div className="mb-4">
                        <Form>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              <strong>1. Nome da Integração</strong>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={integrationName}
                              onChange={(e) =>
                                setIntegrationName(e.target.value)
                              }
                              placeholder="Ex: Calendário Principal, Agenda Pessoal..."
                              required
                            />
                            <Form.Text className="text-muted">
                              Dê um nome descritivo para identificar esta
                              integração
                            </Form.Text>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>
                              <strong>
                                2. Upload do arquivo de credenciais JSON
                              </strong>
                            </Form.Label>
                            <div className="d-flex gap-2 align-items-center">
                              <Form.Control
                                type="file"
                                ref={fileInputRef}
                                accept=".json"
                                onChange={handleFileUpload}
                                className="flex-grow-1"
                              />
                              {credentialsFile && originalFileObject && (
                                <div className="d-flex align-items-center gap-2">
                                  <Badge
                                    bg="success"
                                    className="d-flex align-items-center"
                                  >
                                    <IconWifi size={14} className="me-1" />
                                    {originalFileObject.name}
                                  </Badge>
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => {
                                      setCredentialsFile(null);
                                      setOriginalFileObject(null);
                                      if (fileInputRef.current) {
                                        fileInputRef.current.value = "";
                                      }
                                    }}
                                    title="Remover arquivo"
                                  >
                                    <IconTrash size={12} />
                                  </Button>
                                </div>
                              )}
                            </div>
                            <Form.Text className="text-muted">
                              Selecione o arquivo JSON baixado do Google Cloud
                              Console
                            </Form.Text>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>
                              <strong>3. Calendar ID</strong>
                            </Form.Label>
                            <InputGroup>
                              <Form.Control
                                type="text"
                                value={calendarId}
                                onChange={(e) => setCalendarId(e.target.value)}
                                placeholder="primary"
                              />
                            </InputGroup>
                            <Form.Text className="text-muted">
                              Use "primary" para o calendário principal ou o ID
                              específico do calendário
                            </Form.Text>
                          </Form.Group>

                          <div className="d-flex gap-3 justify-content-center">
                            <Button
                              variant="primary"
                              size="lg"
                              onClick={handleCreateIntegration}
                              disabled={
                                !credentialsFile ||
                                !integrationName.trim() ||
                                uploadingCredentials
                              }
                            >
                              {uploadingCredentials ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                  />
                                  Criando...
                                </>
                              ) : (
                                <>
                                  <IconUpload size={20} className="me-2" />
                                  Criar Integração
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="lg"
                              onClick={() =>
                                window.open(
                                  "https://console.cloud.google.com",
                                  "_blank"
                                )
                              }
                            >
                              <IconWorld size={20} className="me-2" />
                              Google Cloud Console
                            </Button>
                          </div>
                        </Form>
                      </div>
                    </div>
                  ) : (
                    <div className="alert alert-warning">
                      <strong>Plano necessário:</strong> Esta funcionalidade
                      está disponível apenas para contas ativas com planos
                      Founder, PRO ou Business.
                      {!user?.is_active && (
                        <div className="mt-2">
                          <small className="text-danger">
                            Sua conta não está ativa no momento.
                          </small>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Modal.Body>
          </Modal>
        </section>
      )}

      <style jsx global>{`
        .integration-card {
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
          border: 1px solid #e9ecef;
        }

        .integration-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .integration-icon {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Fix para SweetAlert2 dentro de modais Bootstrap */
        .swal-high-zindex {
          z-index: 99999 !important;
        }

        .swal-high-zindex .swal2-container {
          z-index: 99999 !important;
        }

        .swal-high-zindex .swal2-backdrop-show {
          z-index: 99998 !important;
        }

        @media (max-width: 768px) {
          .integration-card .card-footer .d-flex {
            justify-content: center;
          }

          .integration-card .card-footer .d-flex > * {
            margin: 2px;
          }
        }
      `}</style>
    </>
  );
}
